import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

import { FEATURE_AI_PLAYGROUND, type ClinicPlanState } from '../../../../core/services/clinic-plan.rules';
import { ClinicPlanService } from '../../../../core/services/clinic-plan.service';

const SUPPORT_EMAIL = 'support@metasmile.com';

/** Pill colours per state; dark text on pale fills meets AA. */
const STATE_CLASS: Readonly<Record<ClinicPlanState, string>> = {
  active: 'bg-emerald-50 text-emerald-700 border-emerald-100',
  trial: 'bg-blue-50 text-blue-700 border-blue-100',
  grace: 'bg-amber-50 text-amber-800 border-amber-100',
  expired: 'bg-rose-50 text-rose-700 border-rose-100',
  cancelled: 'bg-rose-50 text-rose-700 border-rose-100',
  none: 'bg-slate-50 text-slate-600 border-slate-200',
};

/**
 * Settings › Subscriptions: this branch's clinic-app plan (what it includes,
 * when the period ends, the grace period) and its CRM plan. Plans are set by
 * the platform, so changing one is a request to support.
 */
@Component({
  selector: 'app-branch-plan-card',
  imports: [TranslateModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (plan(); as p) {
      <section class="rounded-[2rem] border border-slate-100 bg-white p-8 shadow-sm" aria-labelledby="bpc-title">
        <div class="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h2 id="bpc-title" class="text-xs font-black uppercase tracking-[0.2em] text-slate-500">
              {{ 'plan_card_title' | translate }}
            </h2>
            <p class="mt-2 text-xl font-bold text-slate-800">{{ p.clinic.plan_name || ('plan_state_none' | translate) }}</p>
          </div>
          <span class="self-start rounded-full border px-3 py-1 text-xs font-bold" [class]="stateClass(p.clinic.state)">
            {{ 'plan_state_' + p.clinic.state | translate }}
          </span>
        </div>

        @if (p.clinic.state === 'grace' && p.clinic.grace_until) {
          <p class="mt-4 rounded-xl bg-amber-50 px-4 py-3 text-sm font-medium text-amber-900" role="status">
            {{ 'plan_card_grace' | translate: { date: date(p.clinic.grace_until) } }}
          </p>
        } @else if (p.clinic.state === 'expired' || p.clinic.state === 'cancelled') {
          <p class="mt-4 rounded-xl bg-rose-50 px-4 py-3 text-sm font-medium text-rose-800" role="status">
            {{ ('plan_card_lapsed_' + p.clinic.state) | translate }}
          </p>
        }

        <dl class="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
          @if (p.is_owner && p.clinic.price != null) {
            <div>
              <dt class="text-[10px] font-black uppercase tracking-widest text-slate-400">{{ 'plan_card_price' | translate }}</dt>
              <dd class="mt-1 text-sm font-semibold text-slate-700">€ {{ p.clinic.price }} / {{ periodLabel(p.clinic.billing_period) }}</dd>
            </div>
          }
          <div>
            <dt class="text-[10px] font-black uppercase tracking-widest text-slate-400">{{ 'plan_card_period_end' | translate }}</dt>
            <dd class="mt-1 text-sm font-semibold text-slate-700">
              {{ p.clinic.end_date ? date(p.clinic.end_date) : ('plan_card_open_ended' | translate) }}
            </dd>
          </div>
          <div>
            <dt class="text-[10px] font-black uppercase tracking-widest text-slate-400">{{ 'plan_card_crm' | translate }}</dt>
            <dd class="mt-1 text-sm font-semibold text-slate-700">
              {{ p.crm.plan_name || ('plan_card_no_crm' | translate) }}
              @if (p.crm.plan_name) {
                <span class="font-normal text-slate-500">· {{ 'plan_state_' + p.crm.state | translate }}</span>
              }
            </dd>
          </div>
        </dl>

        @if (includes().length) {
          <p class="mt-6 text-[10px] font-black uppercase tracking-widest text-slate-400">{{ 'plan_card_includes' | translate }}</p>
          <ul class="mt-2 flex flex-wrap gap-2" role="list">
            @for (item of includes(); track item) {
              <li class="rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-700">{{ item }}</li>
            }
          </ul>
        }

        <p class="mt-6 text-sm text-slate-600">
          {{ 'plan_card_change' | translate }}
          <a class="font-semibold text-primary underline" [href]="'mailto:' + supportEmail">{{ supportEmail }}</a>
        </p>
      </section>
    }
  `,
})
export class BranchPlanCard {
  private readonly plans = inject(ClinicPlanService);
  private readonly translate = inject(TranslateService);

  protected readonly supportEmail = SUPPORT_EMAIL;
  protected readonly plan = this.plans.plan;

  protected readonly includes = computed(() => {
    const clinic = this.plan()?.clinic;
    if (!clinic) return [];
    const features = (clinic.features ?? []).map((f) =>
      f === FEATURE_AI_PLAYGROUND ? this.translate.instant('VOICE_TREATMENT_TAB') : f,
    );
    return [...(clinic.module_names ?? []), ...features];
  });

  protected stateClass(state: ClinicPlanState): string {
    return STATE_CLASS[state] ?? STATE_CLASS.none;
  }

  /** Plain dates (no time zone): reorder the digits rather than parse them. */
  protected date(value: string | null | undefined): string {
    const m = value ? /^(\d{4})-(\d{2})-(\d{2})/.exec(value) : null;
    return m ? `${m[3]}/${m[2]}/${m[1]}` : (value ?? '');
  }

  protected periodLabel(period: string | undefined): string {
    if (!period) return '';
    const key = `plan_period_${period}`;
    const label = this.translate.instant(key);
    return label === key ? period : label;
  }
}
